/* eslint-disable max-lines */
import React, { useState } from 'react';

import { gql, useApolloClient } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { Dialog, FCWithFragments, TimezoneSelect } from '@modernloop/shared/components';
import { PublicError } from '@modernloop/shared/helper-components';
import { CrossIcon } from '@modernloop/shared/icons';
import {
  TimeRange,
  getTimeRangesInTimezone,
  getUniqueTimeRanges,
  mergeTimeRanges,
  sortTimeRanges,
} from '@modernloop/shared/utils';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { max } from 'lodash';

import {
  CandidateDetailsDocument,
  DateTimeRangeInput,
  EditCandidateAvailabilityModal_AvailabilityFragment,
} from 'src/generated/mloop-graphql';

import { ImportAvailabilityButton } from 'src/components/ImportAvailabilityButton';

import { OrgPrefName, useOrgPrefJson } from 'src/hooks/api/org-grpc';
import { useImportCandidateAvailabilities } from 'src/hooks/scheduling/useImportCandidateAvailabilities';
import { useActivityFeedRefetch } from 'src/hooks/useActivityFeedRefetch';
import { useApolloRefetchQuery } from 'src/hooks/useApolloQuery';
import useCompanyHolidays from 'src/hooks/useCompanyHolidays';
import useEmployeeId from 'src/hooks/useEmployeeId';
import { LoggerEvent, useLogEvent } from 'src/hooks/useLogEvent';
import useRefetchScheduleTaskQuery from 'src/hooks/useRefetchScheduleTaskQuery';

import { DEFAULT_WORK_HOURS, TimeBlockWeek, isTimeBlockWeekNotEmpty } from 'src/types/preferenes';

import updateCandidateAvailability from 'src/utils/api/candidate/updateCandidateAvailability';
import { apolloCacheDeleteByQuery } from 'src/utils/apolloHelpers';
import { AxiosError } from 'src/utils/axios';
import { startOfNextDay } from 'src/utils/dateUtils';
import { getTimePeriods } from 'src/utils/getTimePeriods';

import { AvailabilityRow } from 'src/views-new/ScheduleFlow/Steps/CandidateAvailability/AvailabilityRow';

import {
  getErrorText,
  getWarningText,
  hasWarningOrError,
} from 'src/views/booking/BookingCreateView/CandidateAvailabilityStep/candidateAvailabilityUtils';

type Fragments = {
  availability: EditCandidateAvailabilityModal_AvailabilityFragment | undefined;
};

type Props = {
  applicationId: string;
  jobStageId: string;
  taskId?: string;
  candidateTimezone: string;
  onClose: () => void;
};

const EditCandidateAvailabilityModal: FCWithFragments<Fragments, Props> = ({
  applicationId,
  jobStageId,
  taskId,
  candidateTimezone,
  availability,
  onClose,
}): JSX.Element => {
  const { t } = useTranslation();
  const employeeId = useEmployeeId();
  const apolloRefetchQuery = useApolloRefetchQuery();
  const client = useApolloClient();
  const activityFeedRefetch = useActivityFeedRefetch();
  const refetchScheduleTaskQuery = useRefetchScheduleTaskQuery();
  const [importSuccessLabel, setImportSuccessLabel] = useState<string>('');
  const [importWarningLabel, setImportWarningLabel] = useState<string>('');
  const clearWarnings = () => {
    setImportSuccessLabel('');
    setImportWarningLabel('');
  };

  const [importComplete, setImportComplete] = useState<boolean>(false);
  const { logEvent } = useLogEvent();

  const [updatingAvailability, setUpdatingAvailability] = useState<boolean>(false);
  const [updateAvailabilityError, setUpdateAvailabilityError] = useState<AxiosError>();

  const isUpdating = (): boolean => {
    return updatingAvailability;
  };

  const holidays = useCompanyHolidays();

  const [orgWorkHours] = useOrgPrefJson<TimeBlockWeek>(OrgPrefName.OBJ_DEFAULT_WORK_HOURS);

  const [rcEnteredAvailabilities, setRCEnteredAvailabilities] = useState<DateTimeRangeInput[]>(
    availability?.rcEnteredAvailability
      ? getTimeRangesInTimezone(
          sortTimeRanges(
            availability.rcEnteredAvailability.map((a) => ({
              startAt: a.startAt,
              endAt: a.endAt,
            }))
          ),
          candidateTimezone
        )
      : []
  );
  const [candidateEnteredAvailabilities, setCandidateEnteredAvailabilities] = useState<DateTimeRangeInput[]>(
    availability?.candidateEnteredAvailability
      ? getTimeRangesInTimezone(
          sortTimeRanges(
            availability.candidateEnteredAvailability.map((a) => ({
              startAt: a.startAt,
              endAt: a.endAt,
            }))
          ),
          candidateTimezone
        )
      : []
  );

  const { importAvailabilities, importingAvailability } = useImportCandidateAvailabilities({
    applicationId,
    candidateTimezone,
    taskId,
    setImportWarningLabel,
    setImportSuccessLabel,
    onCompleted: (candidateEnteredImports, rcEnteredImports, totalMinutes) => {
      setRCEnteredAvailabilities(
        getUniqueTimeRanges(
          sortTimeRanges(mergeTimeRanges([...rcEnteredAvailabilities, ...rcEnteredImports, ...candidateEnteredImports]))
        )
      );
      logEvent(LoggerEvent.CLIENT_CANDIDATE_AVAILABILITY_IMPORTED, { importedMinutes: totalMinutes });
      setImportComplete(true);
    },
  });

  const [timezone, setTimezone] = useState(candidateTimezone);

  const handleTimezoneChange = (tz: string) => {
    setTimezone(tz);
    setCandidateEnteredAvailabilities(getTimeRangesInTimezone(candidateEnteredAvailabilities, tz));
    setRCEnteredAvailabilities(getTimeRangesInTimezone(rcEnteredAvailabilities, tz));
  };

  const handleAddRCEnteredAvailability = () => {
    const endTime = startOfNextDay(
      max(
        [...rcEnteredAvailabilities, ...candidateEnteredAvailabilities].map((value) => new Date(value.endAt).getTime())
      ) ?? Date.now(),
      timezone
    );

    // If no work hours are set, default to 9am-5pm so we don't block adding availabilities
    const computedWorkHours = isTimeBlockWeekNotEmpty(orgWorkHours) ? orgWorkHours : DEFAULT_WORK_HOURS;
    const timePeriods = getTimePeriods(endTime.toISOString(), computedWorkHours, timezone);
    if (timePeriods.length === 0) {
      return;
    }
    setRCEnteredAvailabilities([
      ...rcEnteredAvailabilities,
      { startAt: timePeriods[0].start, endAt: timePeriods[0].end },
    ]);
  };

  const handleUpdateRCEnteredAvailability = (start: string, end: string, index: number) => {
    const newAvailabilities = [...rcEnteredAvailabilities];
    if (index >= newAvailabilities.length) {
      return;
    }

    newAvailabilities[index] = { startAt: start, endAt: end };
    setRCEnteredAvailabilities(newAvailabilities);
  };

  const handleDeleteRCEnteredAvailability = (index: number) => {
    setRCEnteredAvailabilities(rcEnteredAvailabilities.filter((_, i) => index !== i));
  };

  const handleUpdateCandidateEnteredAvailability = (start: string, end: string, index: number) => {
    const newAvailabilities = [...candidateEnteredAvailabilities];
    if (index >= newAvailabilities.length) {
      return;
    }

    newAvailabilities[index] = { startAt: start, endAt: end };
    setCandidateEnteredAvailabilities(newAvailabilities);
  };

  const handleDeleteCandidateEnteredAvailability = (index: number) => {
    setCandidateEnteredAvailabilities(candidateEnteredAvailabilities.filter((_, i) => index !== i));
  };

  const handleSave = async () => {
    if (!employeeId) {
      return false;
    }

    const mergedAvailability = mergeTimeRanges([...candidateEnteredAvailabilities, ...rcEnteredAvailabilities]).map(
      (a) => ({
        start: a.startAt,
        end: a.endAt,
      })
    );

    setUpdatingAvailability(true);
    return updateCandidateAvailability({
      applicationId,
      jobStageId,
      candidateAvailability: mergedAvailability,
      candidateTimezone: timezone,
      taskId,
    })
      .then(async () => {
        refetchScheduleTaskQuery();
        activityFeedRefetch();
        await apolloRefetchQuery([CandidateDetailsDocument]);
        apolloCacheDeleteByQuery(client.cache, 'candidateAvailability', {
          input: {
            applicationId,
            jobStageId,
          },
        });
        onClose();
        return true;
      })
      .catch((err: AxiosError) => {
        setUpdateAvailabilityError(err);
        return false;
      })
      .finally(() => {
        setUpdatingAvailability(false);
      });
  };

  const renderAvailability = (a: TimeRange, index: number, isCandidateEnteredAvailability: boolean) => {
    const warningOrError = hasWarningOrError(
      { id: '', day: a.startAt, startAt: a.startAt, endAt: a.endAt },
      timezone,
      holidays
    );

    const errorText = warningOrError
      ? getErrorText({
          id: '',
          day: a.startAt,
          startAt: a.startAt,
          endAt: a.endAt,
        })
      : '';

    const warningText = warningOrError
      ? getWarningText({ id: '', day: a.startAt, startAt: a.startAt, endAt: a.endAt }, timezone, holidays)
      : '';

    const deleteHandler = isCandidateEnteredAvailability
      ? () => handleDeleteCandidateEnteredAvailability(index)
      : () => handleDeleteRCEnteredAvailability(index);

    if (!warningOrError) {
      return (
        <Box key={`${a.startAt}-${a.endAt}-${index}`}>
          <AvailabilityRow
            warningText={warningText}
            errorText={errorText}
            availability={{ startAt: a.startAt, endAt: a.endAt }}
            timezone={timezone}
            onChange={(start, end) =>
              isCandidateEnteredAvailability
                ? handleUpdateCandidateEnteredAvailability(start, end, index)
                : handleUpdateRCEnteredAvailability(start, end, index)
            }
            onDelete={deleteHandler}
          />
        </Box>
      );
    }

    return (
      <Box key={`${a.startAt}-${a.endAt}-${index}`}>
        <Alert
          icon={false}
          severity={errorText ? 'error' : 'warning'}
          sx={{
            marginLeft: '-12px',
            marginRight: '-12px',
          }}
        >
          <AvailabilityRow
            availability={{ startAt: a.startAt, endAt: a.endAt }}
            timezone={timezone}
            onChange={(start, end) =>
              isCandidateEnteredAvailability
                ? handleUpdateCandidateEnteredAvailability(start, end, index)
                : handleUpdateRCEnteredAvailability(start, end, index)
            }
            onDelete={deleteHandler}
          />
          {errorText || warningText}
        </Alert>
      </Box>
    );
  };

  const renderError = (): JSX.Element => {
    return (
      <>
        {updateAvailabilityError && (
          <Box sx={{ padding: (theme) => `${theme.spacing(1.5)} ${theme.spacing(2.5)}` }}>
            <PublicError error={updateAvailabilityError} />
          </Box>
        )}
      </>
    );
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('hiring-dashboard-candidates:candidates.editAvailability.title')}
      submitOptions={{
        label: t('hiring-dashboard-common:common.save'),
        onClick: handleSave,
        isDisabled: isUpdating() || importingAvailability,
        isLoading: isUpdating(),
      }}
      cancelOptions={{ label: t('hiring-dashboard-common:common.cancel'), onClick: onClose }}
    >
      <Stack direction="column" spacing={2.5}>
        {renderError()}

        <Box>
          <Typography variant="subtitle1">
            {t('hiring-dashboard-candidates:candidates.editAvailability.candidateTimezone')}
          </Typography>
          <TimezoneSelect fullWidth value={timezone} onTimezoneChange={handleTimezoneChange} disableClearable />
        </Box>

        <Box>
          <Stack spacing={1.5}>
            <Box>
              <Box component="span">
                <Typography component="span" variant="subtitle1">
                  {t('hiring-dashboard-candidates:candidates.editAvailability.currentAvailability')}
                </Typography>
                <Typography component="span" color="text.secondary">
                  {t('hiring-dashboard-candidates:candidates.editAvailability.inCandidateTimezone')}
                </Typography>
              </Box>
              {availability?.lastUpdatedByEmployee?.fullName && (
                <Typography color="text.secondary" variant="body2">
                  {t('hiring-dashboard-candidates:candidates.editAvailability.lastUpdatedBy', {
                    employeeName: availability.lastUpdatedByEmployee.fullName,
                  })}
                </Typography>
              )}
              {importSuccessLabel && (
                <Alert
                  severity="success"
                  action={
                    <CrossIcon
                      onClick={() => {
                        clearWarnings();
                      }}
                      sx={{ cursor: 'pointer' }}
                    />
                  }
                >
                  <Stack direction="row" alignItems="center" display="flex" flex="1">
                    <Box flex="1" justifyContent="center">
                      <Typography variant="body2">{importSuccessLabel}</Typography>
                    </Box>
                  </Stack>
                </Alert>
              )}
              {importWarningLabel && (
                <Alert
                  severity="warning"
                  action={
                    <CrossIcon
                      onClick={() => {
                        clearWarnings();
                      }}
                      sx={{ cursor: 'pointer' }}
                    />
                  }
                >
                  <Stack direction="row" alignItems="center" display="flex" flex="1">
                    <Box flex="1" justifyContent="center">
                      <Typography variant="body2">{importWarningLabel}</Typography>
                    </Box>
                  </Stack>
                </Alert>
              )}
            </Box>

            {candidateEnteredAvailabilities &&
              candidateEnteredAvailabilities.length > 0 &&
              candidateEnteredAvailabilities?.map((a, index) => renderAvailability(a, index, true))}

            {rcEnteredAvailabilities &&
              rcEnteredAvailabilities.length > 0 &&
              rcEnteredAvailabilities?.map((a, index) => renderAvailability(a, index, false))}

            <Stack direction="row" gap={2}>
              <Button
                disabled={importingAvailability || !candidateTimezone}
                sx={{
                  alignSelf: 'start',
                }}
                fullWidth={false}
                size="small"
                onClick={handleAddRCEnteredAvailability}
              >
                {t('hiring-dashboard-candidates:candidates.editAvailability.addNew')}
              </Button>
              <ImportAvailabilityButton
                importingAvailability={importingAvailability}
                importComplete={importComplete}
                importAvailabilities={importAvailabilities}
                clearWarnings={clearWarnings}
                candidateTimezone={candidateTimezone}
                showSmallButton
              />
            </Stack>
          </Stack>
        </Box>
      </Stack>
    </Dialog>
  );
};

EditCandidateAvailabilityModal.fragments = {
  availability: gql`
    fragment EditCandidateAvailabilityModal_availability on CandidateAvailability {
      id
      candidateEnteredAvailability {
        startAt
        endAt
      }
      rcEnteredAvailability {
        startAt
        endAt
      }
      lastUpdatedByEmployee {
        id
        fullName
      }
    }
  `,
};

export default EditCandidateAvailabilityModal;
